import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import {
  Select as AriaSelect,
  Dialog,
  ListBox,
  ListBoxItem,
  Modal,
  ModalOverlay,
  Popover,
  SelectValue,
} from 'react-aria-components';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Label } from '@actual-app/components/label';
import { theme } from '@actual-app/components/theme';
import { tokens } from '@actual-app/components/tokens';
import { View } from '@actual-app/components/view';
import type { IncomeDistributionAllocation } from '@actual-app/finance-engine';

import { FinancialText } from '#components/FinancialText';

import { createCommandId } from './command-id';
import { getFinancialErrorCode } from './errors';
import { IncomeDistributionEditor } from './IncomeDistributionEditor';
import type {
  IncomeDistributionContext,
  IncomeDistributionSelection,
} from './IncomeDistributionEditor';
import { formatMoneyInput, formatProductMoney, parseMoneyInput } from './money';
import type { ProductMoney } from './money';

export type FinancialCommandKind =
  | 'expense'
  | 'emergency-expense'
  | 'extra-income'
  | 'expected-income'
  | 'scheduled-payment'
  | 'edit-movement';

export type FinancialCommandTarget = Readonly<{
  occurrenceKey: string;
  name: string;
  amount: ProductMoney;
  date: string;
  actualFactId?: string;
  accountId?: string;
  factType?: 'income' | 'expense';
}>;

export type FinancialCommandCategory = Readonly<{
  id: string;
  name: string;
  isIncome: boolean;
}>;

type FinancialCommandDialogProps = Readonly<{
  kind: FinancialCommandKind;
  accounts: readonly Readonly<{ id: string; name: string }>[];
  categories?: readonly FinancialCommandCategory[];
  defaultDate: string;
  defaultAccountId?: string;
  maximumAmountMinorUnits?: number;
  target?: FinancialCommandTarget;
  incomeDistribution?: IncomeDistributionContext;
  onClose: (shouldReviewOutcome?: boolean) => void;
  onSubmit: (input: {
    accountId: string;
    amountMinorUnits: number;
    date: string;
    description: string;
    categoryId?: string;
    note?: string;
    distribution?: readonly IncomeDistributionAllocation[];
    commandId: string;
  }) => Promise<void>;
}>;

export function FinancialCommandDialog({
  kind,
  accounts,
  categories = [],
  defaultDate,
  defaultAccountId,
  maximumAmountMinorUnits,
  target,
  incomeDistribution,
  onClose,
  onSubmit,
}: FinancialCommandDialogProps) {
  const { t } = useTranslation();
  const [accountId, setAccountId] = useState(
    target?.accountId ?? defaultAccountId ?? '',
  );
  const [amount, setAmount] = useState(
    target === undefined ? '' : formatMoneyInput(target.amount),
  );
  const [date, setDate] = useState(
    kind === 'edit-movement' ? (target?.date ?? defaultDate) : defaultDate,
  );
  const [description, setDescription] = useState(target?.name ?? '');
  const [categoryId, setCategoryId] = useState('');
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnknownOutcome, setHasUnknownOutcome] = useState(false);
  // A retry of the same payload reuses its command id so a reply lost after
  // the write cannot book the money twice; changing any field starts a new one.
  const lastAttempt = useRef<{ signature: string; commandId: string } | null>(
    null,
  );
  function commandIdFor(payload: unknown): string {
    const signature = JSON.stringify(payload);
    if (lastAttempt.current?.signature !== signature) {
      lastAttempt.current = { signature, commandId: createCommandId() };
    }
    return lastAttempt.current.commandId;
  }
  const [distribution, setDistribution] =
    useState<IncomeDistributionSelection>(undefined);
  const [validationError, setValidationError] = useState<
    | 'account'
    | 'amount'
    | 'available-fund'
    | 'concept'
    | 'already-reconciled'
    | 'account-configuration'
    | 'submit'
    | 'distribution'
    | 'distribution-submit'
    | null
  >(null);
  const isConfirmation =
    kind === 'expected-income' || kind === 'scheduled-payment';
  const factType =
    kind === 'extra-income' || kind === 'expected-income'
      ? 'income'
      : kind === 'edit-movement'
        ? target?.factType
        : 'expense';
  const availableCategories = categories.filter(
    category =>
      factType === undefined || category.isIncome === (factType === 'income'),
  );
  const titleId = `financial-command-${kind}-title`;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting) return;
    const amountMinorUnits = parseMoneyInput(amount);
    if (accountId.length === 0) {
      setValidationError('account');
      return;
    }
    if (amountMinorUnits === null) {
      setValidationError('amount');
      return;
    }
    if (distribution === null) {
      setValidationError('distribution');
      return;
    }
    if (
      maximumAmountMinorUnits !== undefined &&
      amountMinorUnits > maximumAmountMinorUnits
    ) {
      setValidationError('available-fund');
      return;
    }
    if (
      !isConfirmation &&
      description.trim().length === 0 &&
      categoryId.length === 0
    ) {
      setValidationError('concept');
      return;
    }
    setValidationError(null);
    setIsSubmitting(true);
    try {
      const payload = {
        accountId,
        amountMinorUnits,
        date,
        description: description.trim(),
        ...(categoryId.length === 0 ? {} : { categoryId }),
        ...(note.trim().length === 0 ? {} : { note: note.trim() }),
        ...(distribution === undefined || distribution.length === 0
          ? {}
          : { distribution }),
      };
      await onSubmit({ ...payload, commandId: commandIdFor(payload) });
      onClose();
    } catch (error) {
      const errorCode = getFinancialErrorCode(error);
      const isUnknown = errorCode === 'leader-changed';
      setHasUnknownOutcome(isUnknown);
      setValidationError(
        isUnknown
          ? null
          : errorCode === 'already-reconciled'
            ? 'already-reconciled'
            : errorCode === 'invalid-account-configuration' ||
                errorCode === 'configuration-required'
              ? 'account-configuration'
              : distribution !== undefined &&
                  distribution !== null &&
                  distribution.length > 0
                ? 'distribution-submit'
                : 'submit',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ModalOverlay
      isOpen
      isDismissable={!isSubmitting}
      onOpenChange={isOpen => !isOpen && onClose(hasUnknownOutcome)}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 3000,
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
      }}
    >
      <Modal
        style={{
          width: '100%',
          maxWidth: 520,
          maxHeight: '90vh',
          overflowY: 'auto',
          borderRadius: 10,
          backgroundColor: theme.modalBackground,
          color: theme.pageText,
          boxShadow: '0 18px 60px rgba(0, 0, 0, 0.28)',
        }}
      >
        <Dialog aria-labelledby={titleId} style={{ outline: 'none' }}>
          <form onSubmit={handleSubmit} style={{ padding: 20 }}>
            <View style={{ gap: 6, marginBottom: 18 }}>
              <h2 id={titleId} style={{ margin: 0, fontSize: 22 }}>
                <CommandTitle kind={kind} />
              </h2>
              {target !== undefined && (
                <View style={{ gap: 2, color: theme.pageTextSubdued }}>
                  <span>{target.name}</span>
                  <FinancialText>
                    {formatProductMoney(target.amount)} · {target.date}
                  </FinancialText>
                </View>
              )}
            </View>

            <fieldset
              disabled={isSubmitting || hasUnknownOutcome}
              aria-labelledby={titleId}
              style={{
                display: 'grid',
                gap: 14,
                border: 0,
                padding: 0,
                margin: 0,
                minWidth: 0,
              }}
            >
              <label htmlFor="financial-command-amount">
                <Label
                  title={<AmountLabel kind={kind} />}
                  style={{ display: 'block', textAlign: 'left' }}
                />
                <Input
                  id="financial-command-amount"
                  autoFocus
                  inputMode="decimal"
                  value={amount}
                  onChange={event => {
                    setAmount(event.target.value);
                    setDistribution(undefined);
                  }}
                  style={{ width: '100%', minHeight: 42 }}
                />
              </label>

              <label htmlFor="financial-command-date">
                <Label
                  title={<DateLabel kind={kind} />}
                  style={{ display: 'block', textAlign: 'left' }}
                />
                <Input
                  id="financial-command-date"
                  type="date"
                  value={date}
                  onChange={event => setDate(event.target.value)}
                  style={{ width: '100%', minHeight: 42 }}
                />
              </label>

              <label htmlFor="financial-command-account">
                <Label
                  title={<Trans>Account</Trans>}
                  style={{ display: 'block', textAlign: 'left' }}
                />
                <AriaSelect
                  id="financial-command-account"
                  aria-label={t('Account')}
                  selectedKey={accountId || null}
                  onSelectionChange={key => setAccountId(String(key))}
                  style={{ width: '100%' }}
                >
                  <Button
                    style={{
                      width: '100%',
                      minHeight: 42,
                      justifyContent: 'space-between',
                    }}
                  >
                    <SelectValue>
                      {({ selectedText }) =>
                        selectedText ?? <Trans>Select an account</Trans>
                      }
                    </SelectValue>
                  </Button>
                  <FinancialAccountList accounts={accounts} />
                </AriaSelect>
              </label>

              <label htmlFor="financial-command-category">
                <Label
                  title={<Trans>Category (optional)</Trans>}
                  style={{ display: 'block', textAlign: 'left' }}
                />
                <AriaSelect
                  id="financial-command-category"
                  aria-label={t('Category (optional)')}
                  selectedKey={categoryId || null}
                  onSelectionChange={key => setCategoryId(String(key))}
                  style={{ width: '100%' }}
                >
                  <Button
                    style={{
                      width: '100%',
                      minHeight: 42,
                      justifyContent: 'space-between',
                    }}
                  >
                    <SelectValue>
                      {({ selectedText }) =>
                        selectedText ?? <Trans>Select a category</Trans>
                      }
                    </SelectValue>
                  </Button>
                  <FinancialCategoryList categories={availableCategories} />
                </AriaSelect>
              </label>

              <label htmlFor="financial-command-description">
                <Label
                  title={
                    isConfirmation ? (
                      <Trans>Concept (optional)</Trans>
                    ) : (
                      <Trans>Concept</Trans>
                    )
                  }
                  style={{ display: 'block', textAlign: 'left' }}
                />
                <Input
                  id="financial-command-description"
                  value={description}
                  onChange={event => setDescription(event.target.value)}
                  style={{ width: '100%', minHeight: 42 }}
                />
              </label>

              <label htmlFor="financial-command-note">
                <Label
                  title={<Trans>Note (optional)</Trans>}
                  style={{ display: 'block', textAlign: 'left' }}
                />
                <Input
                  id="financial-command-note"
                  value={note}
                  onChange={event => setNote(event.target.value)}
                  style={{ width: '100%', minHeight: 42 }}
                />
              </label>
            </fieldset>

            {incomeDistribution !== undefined &&
              (kind === 'expected-income' || kind === 'extra-income') && (
                <IncomeDistributionEditor
                  key={amount}
                  context={incomeDistribution}
                  source={kind === 'expected-income' ? 'fixed' : 'extra'}
                  amountMinorUnits={parseMoneyInput(amount) ?? 0}
                  isDisabled={isSubmitting || hasUnknownOutcome}
                  onChange={setDistribution}
                />
              )}

            {hasUnknownOutcome && (
              <p role="alert">
                <Trans>
                  The result is unknown because the database tab closed. Retry
                  the same request safely, or review recent movements before
                  making another entry.
                </Trans>
              </p>
            )}
            {validationError !== null && (
              <p
                role="alert"
                style={{ color: theme.errorText, marginBottom: 0 }}
              >
                <CommandValidationError error={validationError} />
              </p>
            )}

            <View
              style={{
                marginTop: 20,
                flexDirection: 'row',
                justifyContent: 'flex-end',
                gap: 10,
                [`@media (max-width: ${tokens.breakpoint_small})`]: {
                  flexDirection: 'column-reverse',
                },
              }}
            >
              <Button
                type="button"
                isDisabled={isSubmitting}
                onPress={() => onClose(hasUnknownOutcome)}
                style={{ minHeight: 42 }}
              >
                {hasUnknownOutcome ? (
                  <Trans>Review movements</Trans>
                ) : (
                  <Trans>Cancel</Trans>
                )}
              </Button>
              <ButtonWithLoading
                type="submit"
                variant="primary"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                style={{ minHeight: 42 }}
              >
                {hasUnknownOutcome ? (
                  <Trans>Retry same request</Trans>
                ) : distribution !== undefined &&
                  distribution !== null &&
                  distribution.length > 0 ? (
                  <Trans>Confirm income and distribution</Trans>
                ) : (
                  <CommandSubmitLabel kind={kind} />
                )}
              </ButtonWithLoading>
            </View>
          </form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

function FinancialCategoryList({
  categories,
}: {
  categories: readonly FinancialCommandCategory[];
}) {
  return (
    <Popover style={{ zIndex: 3100, minWidth: 240 }}>
      <ListBox
        items={categories}
        style={{
          padding: 6,
          borderRadius: 6,
          backgroundColor: theme.menuBackground,
          color: theme.menuItemText,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.22)',
        }}
      >
        {category => (
          <ListBoxItem
            id={category.id}
            textValue={category.name}
            style={{ padding: 10, cursor: 'pointer', borderRadius: 4 }}
          >
            {category.name}
          </ListBoxItem>
        )}
      </ListBox>
    </Popover>
  );
}

function FinancialAccountList({
  accounts,
}: {
  accounts: readonly Readonly<{ id: string; name: string }>[];
}) {
  return (
    <Popover style={{ zIndex: 3100, minWidth: 240 }}>
      <ListBox
        items={accounts}
        style={{
          padding: 6,
          borderRadius: 6,
          backgroundColor: theme.menuBackground,
          color: theme.menuItemText,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.22)',
        }}
      >
        {account => (
          <ListBoxItem
            id={account.id}
            textValue={account.name}
            style={{ padding: 10, cursor: 'pointer', borderRadius: 4 }}
          >
            {account.name}
          </ListBoxItem>
        )}
      </ListBox>
    </Popover>
  );
}

function CommandTitle({ kind }: { kind: FinancialCommandKind }) {
  switch (kind) {
    case 'expense':
      return <Trans>Record expense</Trans>;
    case 'emergency-expense':
      return <Trans>Use emergency fund</Trans>;
    case 'extra-income':
      return <Trans>Record extra income</Trans>;
    case 'expected-income':
      return <Trans>Confirm expected income</Trans>;
    case 'scheduled-payment':
      return <Trans>Record scheduled payment</Trans>;
    case 'edit-movement':
      return <Trans>Edit movement</Trans>;
    default:
      return assertNever(kind);
  }
}

function AmountLabel({ kind }: { kind: FinancialCommandKind }) {
  return kind === 'expected-income' ? (
    <Trans>Amount received</Trans>
  ) : kind === 'scheduled-payment' ? (
    <Trans>Amount paid</Trans>
  ) : (
    <Trans>Amount</Trans>
  );
}

function DateLabel({ kind }: { kind: FinancialCommandKind }) {
  return kind === 'expected-income' ? (
    <Trans>Date received</Trans>
  ) : kind === 'scheduled-payment' ? (
    <Trans>Date paid</Trans>
  ) : (
    <Trans>Date</Trans>
  );
}

function CommandSubmitLabel({ kind }: { kind: FinancialCommandKind }) {
  switch (kind) {
    case 'expense':
      return <Trans>Record expense</Trans>;
    case 'emergency-expense':
      return <Trans>Record emergency expense</Trans>;
    case 'extra-income':
      return <Trans>Record income</Trans>;
    case 'expected-income':
      return <Trans>Confirm income</Trans>;
    case 'scheduled-payment':
      return <Trans>Record payment</Trans>;
    case 'edit-movement':
      return <Trans>Save changes</Trans>;
    default:
      return assertNever(kind);
  }
}

function CommandValidationError({
  error,
}: {
  error:
    | 'account'
    | 'amount'
    | 'available-fund'
    | 'concept'
    | 'already-reconciled'
    | 'account-configuration'
    | 'submit'
    | 'distribution'
    | 'distribution-submit';
}) {
  switch (error) {
    case 'account':
      return <Trans>Select an account.</Trans>;
    case 'amount':
      return <Trans>Enter a valid amount greater than zero.</Trans>;
    case 'available-fund':
      return <Trans>The amount exceeds the available emergency fund.</Trans>;
    case 'concept':
      return <Trans>Enter a concept or select a category.</Trans>;
    case 'already-reconciled':
      return <Trans>This scheduled movement was already recorded.</Trans>;
    case 'account-configuration':
      return <Trans>Select an active account and try again.</Trans>;
    case 'submit':
      return <Trans>This movement could not be saved. Try again.</Trans>;
    case 'distribution':
      return (
        <Trans>
          Distribution must fit within this income and each goal's remaining
          target.
        </Trans>
      );
    case 'distribution-submit':
      return (
        <Trans>
          Income and savings could not be saved. Review the amounts and try
          again.
        </Trans>
      );
    default:
      return assertNever(error);
  }
}

function assertNever(value: never): never {
  throw new Error(`Unexpected financial command value: ${String(value)}`);
}
